import offlineService from './offline';

const API_BASE_URL = '/api';

// Offline support
const isMutation = (method) => ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  getToken() {
    return localStorage.getItem('auth_token');
  }

  // Helper: build FormData from object (skip null/undefined)
  buildFormData(data) {
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    });
    return fd;
  }

  // Helper: check if value is a File object
  hasFile(data) {
    return Object.values(data).some(v => v instanceof File);
  }

  // Generic request method with error handling
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();
    
    // If body has FormData, don't set Content-Type (browser sets it with boundary)
    const isFormData = options.body instanceof FormData;
    
    const config = {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
      ...options,
    };

    // Don't stringify FormData
    if (isFormData) {
      delete config.headers['Content-Type']; // Let browser set multipart boundary
    } else if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Auto-logout jika token expired
        if (response.status === 401 && errorData.error && errorData.error.includes('Token expired')) {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        // Gabungkan detail validasi (Joi) supaya error tidak samar
        let message = errorData.error || `HTTP error! status: ${response.status}`;
        if (Array.isArray(errorData.details) && errorData.details.length) {
          message = `${message}: ${errorData.details.join(' · ')}`;
        }
        throw new Error(message);
      }

      const responseData = await response.json();
      
      // Cache GET responses for offline (non-FormData)
      if (!isFormData && !isMutation(options.method)) {
        offlineService.setCached(url, responseData).catch(() => {});
      }
      
      return responseData;
    } catch (error) {
      // Offline support: queue mutations, serve cached GET
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const method = options.method || 'GET';
        if (isMutation(method) && !isFormData) {
          const bodyData = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
          await offlineService.queueMutation(method, url, bodyData);
          console.warn('📱 Offline: mutation queued for', url);
          return { _offline: true, _queued: true, message: 'Data akan dikirim saat koneksi kembali' };
        } else if (!isMutation(method)) {
          const cached = await offlineService.getCached(url);
          if (cached) {
            console.warn('📱 Offline: serving cached data for', url);
            return cached;
          }
        }
      }
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Activity CRUD operations
  async getActivities(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/activities?${queryString}`);
  }

  async getActivity(id) {
    return this.request(`/activities/${id}`);
  }

  async createActivity(activityData) {
    const body = this.hasFile(activityData) ? this.buildFormData(activityData) : activityData;
    return this.request('/activities', {
      method: 'POST',
      body,
    });
  }

  async updateActivity(id, activityData) {
    const body = this.hasFile(activityData) ? this.buildFormData(activityData) : activityData;
    return this.request(`/activities/${id}`, {
      method: 'PUT',
      body,
    });
  }

  async deleteActivity(id) {
    return this.request(`/activities/${id}`, {
      method: 'DELETE',
    });
  }

  // Statistics
  async getStats() {
    return this.request('/activities/stats');
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }
}

export const apiService = new ApiService();
export default apiService;
